/**
 * screens/modals/TransactionModal
 * ──────────────────────────────────────────────────────────────────────────
 * One modal for all four transaction types: spend, collection, deposit,
 * replenishment. They differ only in which fields matter, and the differences
 * are declared in TYPE_CONFIG below rather than in four near-identical files.
 *
 * TWO RULES THIS MODAL ENFORCES BEFORE THE REQUEST GOES OUT:
 *  - Every SPEND requires a category. The API refuses one without it
 *    (ERR_CATEGORY_REQUIRED), and so does this form — with an instruction
 *    rather than an error, because the user has not done anything wrong yet.
 *  - Receipt evidence is asked for by default when the org's `receipt_required`
 *    rule is on. Its presence is also what the `noReceipt` approval rule tests,
 *    so leaving it blank may route the spend to an approver.
 *
 * WHAT THE RESULT MEANS. A recorded spend may come back with an `approval`
 * attached, which means the engine HELD it and nothing has moved. Saying
 * "Spend recorded" there would be false, so the toast reads "Sent for approval"
 * and names the rule that caught it.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { useMemo, useState } from 'react';
import { ChipRow, Input, Modal, Textarea } from '../../components';
import { toastWarn } from '../../components/Toast';
import { parseMajorToMinor } from '../../lib/money';
import { recordTransaction } from '../../services/cash/cash.service';
import type { TxnType } from '../../schemas/cash';
import { useConfig } from '../../context/ConfigContext';
import { useShell } from '../../context/ShellContext';
import { useModalSubmit } from './useModalSubmit';

/** What each type is called, and which fields it needs. */
const TYPE_CONFIG: Record<
  string,
  {
    title: string;
    hint: string;
    /** A spend must carry a category; nothing else does. */
    needsCategory: boolean;
    /** A collection settles something Billing says is owed. */
    needsOwedRef: boolean;
    verb: string;
  }
> = {
  spend: {
    title: 'Log a spend',
    hint: 'Money leaving custody. Every spend carries a category.',
    needsCategory: true,
    needsOwedRef: false,
    verb: 'Spend recorded',
  },
  collection: {
    title: 'Record a collection',
    hint: 'Money collected on the company’s behalf.',
    needsCategory: false,
    needsOwedRef: true,
    verb: 'Collection recorded',
  },
  deposit: {
    title: 'Record a deposit',
    hint: 'Cash banked out of custody.',
    needsCategory: false,
    needsOwedRef: false,
    verb: 'Deposit recorded',
  },
  replenishment: {
    title: 'Replenish',
    hint: 'Topping the assignment back up to its limit.',
    needsCategory: false,
    needsOwedRef: false,
    verb: 'Replenishment recorded',
  },
};

export interface TransactionModalProps {
  assignmentId: string;
  txnType: TxnType;
  /** The assignment's currency, so the amount is parsed at the right scale. */
  currency: string;
}

export function TransactionModal({ assignmentId, txnType, currency }: TransactionModalProps) {
  const { closeModal } = useShell();
  const { activeCategories, rules } = useConfig();

  const config = TYPE_CONFIG[txnType] ?? TYPE_CONFIG.spend!;

  const [amountText, setAmountText] = useState('');
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [memo, setMemo] = useState('');
  const [documentRef, setDocumentRef] = useState('');
  const [owedRef, setOwedRef] = useState('');

  const categoryChips = useMemo(
    () =>
      activeCategories.map((category) => ({
        value: category.id,
        label: category.code,
      })),
    [activeCategories],
  );

  const { submitting, run } = useModalSubmit({
    validate: () => {
      const minor = parseMajorToMinor(amountText, currency);
      if (minor === null) return 'Enter an amount.';
      if (minor === '0') return 'An amount of zero records nothing.';
      if (config.needsCategory && categoryChips.length === 0) {
        return 'Add a spend category in Settings first — every spend needs one.';
      }
      if (config.needsCategory && !categoryId) return 'Every spend needs a category.';
      // The org's rule, surfaced as an instruction rather than a hard block:
      // the API accepts it, and a missing receipt may simply route the spend to
      // an approver via the `noReceipt` rule.
      if (config.needsCategory && rules?.receipt_required && !documentRef.trim()) {
        return 'This workspace asks for a receipt on spends. Attach one, or it will need approval.';
      }
      return null;
    },
    submit: () =>
      recordTransaction({
        assignment_id: assignmentId,
        txn_type: txnType,
        amount_minor: parseMajorToMinor(amountText, currency)!,
        ...(categoryId ? { category_id: categoryId } : {}),
        ...(memo.trim() ? { memo: memo.trim() } : {}),
        ...(documentRef.trim() ? { document_ref: documentRef.trim() } : {}),
        ...(owedRef.trim() ? { owed_ref: owedRef.trim() } : {}),
      }),
    title: config.verb,
    fallback: 'The ledger has been updated.',
    onDone: (result) => {
      // The engine HELD it: nothing has moved, and the confirmation must not
      // imply otherwise. A second, amber line names the rule that caught it.
      if (result.approval) {
        toastWarn(
          'Sent for approval',
          result.approval.rule_summary ??
            `${result.approval.needs} approval${result.approval.needs === 1 ? '' : 's'} required before this moves.`,
        );
      }
    },
  });

  return (
    <Modal
      visible
      title={config.title}
      hint={config.hint}
      onClose={closeModal}
      primaryLabel={config.title}
      onPrimary={run}
      primaryLoading={submitting}
    >
      <Input
        label="Amount"
        required
        value={amountText}
        onChangeText={setAmountText}
        placeholder="0.00"
        prefix={currency}
        numeric
      />

      {config.needsCategory && (
        <ChipRow
          label="Category"
          required
          hint="Every spend carries one. Deactivated categories are not offered."
          options={categoryChips}
          value={categoryId}
          onChange={setCategoryId}
        />
      )}

      {config.needsOwedRef && (
        <Input
          label="Owed reference"
          hint="What this collection settles, from Billing."
          value={owedRef}
          onChangeText={setOwedRef}
          placeholder="bil_jr_01205"
          monospace
        />
      )}

      <Input
        label="Evidence"
        hint={
          config.needsCategory
            ? 'A receipt reference. Its absence is what the no-receipt rule tests.'
            : 'A reference for the paperwork, if there is any.'
        }
        value={documentRef}
        onChangeText={setDocumentRef}
        placeholder="rcpt_00841"
        monospace
      />

      <Textarea
        label="Memo"
        value={memo}
        onChangeText={setMemo}
        placeholder="What it was for"
        maxLength={240}
        rows={2}
      />
    </Modal>
  );
}

export default TransactionModal;
